import React, { useEffect, useRef, useState } from 'react';

import { clampToCellBounds } from '../geometry';
import './TextBlockLayer.css';

// Block x/y/width/height are stored in inches but rendered as a % of this
// cell's usable area (usableWidthIn/usableHeightIn), not a fixed
// px-per-inch constant — the canvas is responsive (see PrintableMasterGrid)
// and @media print renders the same cell at true physical size, so every
// conversion below is derived from this layer's actual rendered pixel box,
// not an assumed 96px/inch.
export default function TextBlockLayer({
  blocks,
  setBlocks,
  editable,
  selectedIds,
  selectionCount,
  onSelect,
  onGroupDragCandidate,
  showBoundaries,
  usableWidthIn,
  usableHeightIn,
}) {
  const containerRef = useRef(null);
  const textRefs = useRef({});
  const [activeId, setActiveId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, blockX: 0, blockY: 0 });

  const startDrag = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    // Ctrl/Cmd+click and Shift+click are selection-only gestures (toggle /
    // extend) — they never start a drag.
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onSelect(id, e);
      return;
    }
    // A plain mousedown on a block that's already part of a multi-selection
    // is ambiguous until the mouse actually moves (click collapses the
    // selection to just this block; click+drag moves the whole selection)
    // — deferred entirely to PrintableMasterGrid's group-drag candidate
    // machinery.
    if (selectionCount > 1 && selectedIds.includes(id)) {
      onGroupDragCandidate(id, e);
      return;
    }
    onSelect(id, e);
    setActiveId(id);
    const block = blocks.find((b) => b.id === id);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, blockX: block.x, blockY: block.y };
  };

  useEffect(() => {
    if (!editable || activeId === null) return undefined;

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      const dxIn = ((e.clientX - dragStart.current.mouseX) / rect.width) * usableWidthIn;
      const dyIn = ((e.clientY - dragStart.current.mouseY) / rect.height) * usableHeightIn;
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== activeId) return b;
          // Clamped every move, not just on drop, so the block visibly
          // stops at the cell border instead of the cursor outrunning it.
          const { x, y } = clampToCellBounds(
            dragStart.current.blockX + dxIn,
            dragStart.current.blockY + dyIn,
            b.width,
            b.height,
            usableWidthIn,
            usableHeightIn
          );
          return { ...b, x, y };
        })
      );
    };
    const handleUp = () => setActiveId(null);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [activeId, editable, setBlocks, usableWidthIn, usableHeightIn]);

  const handleDoubleClick = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    setEditingId(id);
    onSelect(id, e);
    setActiveId(null);
  };

  const handleTextChange = (e, id) => {
    const newText = e.target.value;
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, text: newText } : b)));
  };

  const handleTextBlur = () => setEditingId(null);
  const handleTextKeyDown = (e) => {
    if (e.key === 'Escape') setEditingId(null);
  };

  // Auto-measure rendered text size and store it in inches, derived from
  // this container's actual pixel box (not a fixed px-per-inch constant).
  // print_old measured these but a disconnected serializeForMongo silently
  // dropped them on save — this rebuild's serializeTextBlock includes them.
  useEffect(() => {
    if (!editable || !setBlocks) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0 || usableWidthIn <= 0 || usableHeightIn <= 0) return;
    const pxPerInchX = rect.width / usableWidthIn;
    const pxPerInchY = rect.height / usableHeightIn;

    const updates = [];
    blocks.forEach((block) => {
      const el = textRefs.current[block.id];
      if (!el) return;
      const elRect = el.getBoundingClientRect();
      const widthIn = elRect.width / pxPerInchX;
      const heightIn = elRect.height / pxPerInchY;
      if (Math.abs((block.width || 0) - widthIn) > 0.01 || Math.abs((block.height || 0) - heightIn) > 0.01) {
        updates.push({ id: block.id, width: widthIn, height: heightIn });
      }
    });
    if (updates.length > 0) {
      setBlocks((prev) =>
        prev.map((b) => {
          const update = updates.find((u) => u.id === b.id);
          return update ? { ...b, ...update } : b;
        })
      );
    }
  });

  return (
    <div ref={containerRef} className="ss-print__text-layer">
      {blocks.map((b) => {
        const isEditing = editingId === b.id;
        // Defensive clamp at render time too — not just during drag — so a
        // stale stored position (an old save, or a cell that shrank after
        // rows/padding changed) can never render outside the cell border.
        const { x: renderX, y: renderY } = clampToCellBounds(b.x, b.y, b.width, b.height, usableWidthIn, usableHeightIn);
        const leftPct = usableWidthIn > 0 ? (renderX / usableWidthIn) * 100 : 0;
        const topPct = usableHeightIn > 0 ? (renderY / usableHeightIn) * 100 : 0;
        const commonStyle = {
          left: `${leftPct}%`,
          top: `${topPct}%`,
          fontSize: b.fontSize,
          color: b.color,
          fontFamily: b.fontFamily,
          fontWeight: b.fontWeight,
          fontStyle: b.fontStyle,
          textAlign: b.textAlign,
        };

        return isEditing ? (
          <textarea
            key={b.id}
            className="ss-print__text-block-editor"
            value={b.text}
            onChange={(e) => handleTextChange(e, b.id)}
            onBlur={handleTextBlur}
            onKeyDown={handleTextKeyDown}
            autoFocus
            style={commonStyle}
          />
        ) : (
          <div
            key={b.id}
            ref={(el) => {
              textRefs.current[b.id] = el;
            }}
            className={[
              'ss-print__text-block',
              editable ? 'is-editable' : '',
              selectedIds.includes(b.id) ? 'is-selected' : '',
              showBoundaries ? 'show-boundaries' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={commonStyle}
            onMouseDown={(e) => startDrag(e, b.id)}
            onDoubleClick={(e) => handleDoubleClick(e, b.id)}
          >
            {b.text}
          </div>
        );
      })}
    </div>
  );
}
